import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BsArrowLeft,
  BsEnvelope,
  BsShieldLock,
} from 'react-icons/bs';
import './Profile.css';
import '../transaction-monitoring/TransactionMonitoring.css';
import { ConsolePageHeader } from '../../components/shared/ListConsoleLayout';
import Loader from '../../components/shared/Loader';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../store/authStore';
import type { PlatformUserUpdateRequest } from '../user-management/models/types';
import {
  validateEmail,
} from '../../utils/validation';
import { ENTITY_STATUS_SELECT_OPTIONS } from '../../utils/status';
import { toCapitalizedWords } from '../../utils/helpers';
import { fetchProfileForUser, updatePlatformUser } from './api/api';
import {
  ProfileField,
  ProfileModal,
  ProfileSectionCard,
} from './components/ProfileModal';
import type { EditModal, ProfileRecord } from './models/types';
import {
  buildFallbackProfile,
  formatDisplayStatus,
  formatTimestamp,
  getInitials,
  isActiveStatus,
} from './utils/utils';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthStore(state => state.user);
  const login = useAuthStore(state => state.login);
  const token = useAuthStore(state => state.token);

  const [profile, setProfile] = useState<ProfileRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<EditModal>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string>>({});
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [accessForm, setAccessForm] = useState({ status: 'Active' });

  const isAdmin = (authUser?.userType ?? 'ADMIN') === 'ADMIN';
  // const idLabel = isAdmin ? 'Admin ID' : 'User ID';
  // const pageSub = isAdmin ? 'Logged-in administrator details' : 'Logged-in user details';

  const fallbackProfile = useMemo(
    () => (authUser ? buildFallbackProfile(authUser) : null),
    [authUser]
  );

  const displayProfile = profile ?? fallbackProfile;

  const fetchProfile = useCallback(async () => {
    if (!authUser) {
      setProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const loaded = await fetchProfileForUser(authUser);
      setProfile(loaded);
      const phone = loaded.phone === '—' ? '' : loaded.phone;
      if (phone && phone !== authUser.phone) {
        login({ ...authUser, phone }, token ?? '');
      }
    } catch (err: unknown) {
      setProfile(buildFallbackProfile(authUser));
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to load full profile details.',
      );
    } finally {
      setLoading(false);
    }
  }, [authUser, login, token]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const openProfileEdit = () => {
    if (!displayProfile) return;
    setModalError(null);
    setProfileFieldErrors({});
    setProfileForm({
      name: displayProfile.name === '—' ? '' : displayProfile.name,
      email: displayProfile.email === '—' ? '' : displayProfile.email,
      phone: displayProfile.phone === '—' ? '' : displayProfile.phone,
    });
    setEditModal('profile');
  };

  const openAccessEdit = () => {
    if (!displayProfile) return;
    setModalError(null);
    setAccessForm({ status: displayProfile.status });
    setEditModal('access');
  };

  const syncAuthUser = (next: ProfileRecord) => {
    if (!authUser) return;
    login(
      {
        ...authUser,
        fullName: next.name,
        email: next.email,
        phone: next.phone === '—' ? '' : next.phone,
        status: next.status.toUpperCase(),
        adminType: next.role,
        role: next.role,
      },
      token ?? ''
    );
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser || !displayProfile) return;

    setModalError(null);

    const fieldErrors: Record<string, string> = {};
    if (!profileForm.name.trim()) fieldErrors.name = 'Name is required';
    const emailError = validateEmail(profileForm.email, { required: true });
    if (emailError) fieldErrors.email = emailError;
    if (Object.keys(fieldErrors).length > 0) {
      setProfileFieldErrors(fieldErrors);
      return;
    }
    setProfileFieldErrors({});
    setSaving(true);

    try {
      const payload: PlatformUserUpdateRequest = {
        userId: displayProfile.id,
        userType: displayProfile.userType,
        name: profileForm.name.trim(),
        email: profileForm.email.trim(),
      };

      await updatePlatformUser(payload);

      const next: ProfileRecord = {
        ...displayProfile,
        name: payload.name ?? displayProfile.name,
        email: payload.email ?? displayProfile.email,
        phone: profileForm.phone.trim() || '—',
        updatedAt: new Date().toISOString(),
        otpEnabled: true,
      };
      setProfile(next);
      syncAuthUser(next);
      setEditModal(null);
    } catch (err: unknown) {
      setModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to update profile.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAccessSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authUser || !displayProfile) return;

    setModalError(null);
    setSaving(true);

    try {
      const payload: PlatformUserUpdateRequest = {
        userId: displayProfile.id,
        userType: displayProfile.userType,
        status: accessForm.status.trim().toUpperCase(),
        roleId: displayProfile.roleId,
      };

      await updatePlatformUser(payload);

      const next: ProfileRecord = {
        ...displayProfile,
        status: formatDisplayStatus(payload.status),
        updatedAt: new Date().toISOString(),
      };
      setProfile(next);
      syncAuthUser(next);
      setEditModal(null);
    } catch (err: unknown) {
      setModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to update access settings.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!authUser) {
    return (
      <div className="dh-page">
        <p className="profile-page-sub">Please sign in to view your profile.</p>
      </div>
    );
  }

  const active = isActiveStatus(displayProfile?.status);
  const otpEnabled = displayProfile?.otpEnabled ?? true;

  return (
    <div className="tm-page dh-page">
      <ConsolePageHeader title="My Profile" />

      {error && <div className="profile-error-banner">{error}</div>}

      {loading && !displayProfile ? (
        <div className="profile-loading-wrap">
          <Loader size="md" label="Loading profile..." />
        </div>
      ) : displayProfile ? (
        <>
          <div className="profile-summary-card">
            <div className="profile-summary-main">
              <div className="profile-avatar">{getInitials(displayProfile.name)}</div>
              <div className="profile-summary-info">
                <div className="profile-name-row">
                  <h2 className="profile-name">{toCapitalizedWords(displayProfile.name)}</h2>
                  <span className={`profile-status-badge ${active ? 'active' : 'inactive'}`}>
                    {active ? 'Active' : displayProfile.status}
                  </span>
                </div>
                {/* <p className="profile-role-line">{formatRoleLabel(displayProfile.role)}</p> */}
                <div className="profile-meta-row">
                  <span className="profile-meta-item">
                    <BsEnvelope size={14} />
                    {displayProfile.email}
                  </span>
                  <span className="profile-meta-item">
                    <BsShieldLock size={14} />
                    OTP {otpEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" className="profile-back-btn" onClick={() => navigate('/dashboard/transaction-dashboard')}>
              <BsArrowLeft /> Back
            </button>
          </div>

          <div className="profile-cards-grid">
            <ProfileSectionCard
              title="Profile details"
              subtitle="Name, email, and phone"
              onEdit={isAdmin ? openProfileEdit : undefined}
            >
              <ProfileField label="Name" value={toCapitalizedWords(displayProfile.name)} />
              <ProfileField label="Email" value={displayProfile.email} />
              <ProfileField label="Phone" value={displayProfile.phone || '—'} />
              {/* <ProfileField label={idLabel} value={displayProfile.id} /> */}
            </ProfileSectionCard>

            <ProfileSectionCard
              title="Access settings"
              subtitle="Status and OTP controls"
              onEdit={isAdmin ? openAccessEdit : undefined}
              footer={
                <div className="profile-card-badges">
                  <span className={`profile-status-badge ${active ? 'active' : 'inactive'}`}>
                    {active ? 'Active' : displayProfile.status}
                  </span>
                  <span className={`profile-status-badge ${otpEnabled ? 'otp-enabled' : 'otp-disabled'}`}>
                    OTP {otpEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              }
            >
              <ProfileField label="Status" value={displayProfile.status} />
              <ProfileField label="OTP status" value={otpEnabled ? 'Enabled' : 'Disabled'} />
              {/* <ProfileField label="Role" value={displayProfile.role} /> */}
              {/* <ProfileField label="Last updated" value={formatTimestamp(displayProfile.updatedAt)} /> */}
            </ProfileSectionCard>

            <ProfileSectionCard
              title="Password"
              subtitle="Update login password"
              onEdit={() => {
                setModalError(null);
                setEditModal('password');
              }}
            >
              <ProfileField label="Password" value="••••••••" />
              <ProfileField label="Policy" value="Min 8, max 128 characters" />
            </ProfileSectionCard>

            {/* <ProfileSectionCard
              title="Audit"
              subtitle="Read-only timestamps"
              editable={false}
            >
              <ProfileField label="Created at" value={formatTimestamp(displayProfile.createdAt)} />
              <ProfileField label="Updated at" value={formatTimestamp(displayProfile.updatedAt)} />
            </ProfileSectionCard> */}
          </div>
        </>
      ) : null}

      {editModal === 'profile' && (
        <ProfileModal
          title="Edit Profile Details"
          onClose={() => setEditModal(null)}
          disabled={saving}
        >
          <form onSubmit={handleProfileSave} className="fin-form">
            <div className="fin-form-grid">
              <label className="fin-label">
                <span>Full Name</span>
                <Input
                  className={cn(profileFieldErrors.name && 'border-destructive')}
                  value={profileForm.name}
                  onChange={e => {
                    setProfileForm(prev => ({ ...prev, name: e.target.value }));
                    if (profileFieldErrors.name) {
                      setProfileFieldErrors(prev => ({ ...prev, name: '' }));
                    }
                  }}
                  required
                />
                {profileFieldErrors.name && <p className="cm-err">{profileFieldErrors.name}</p>}
              </label>
              <label className="fin-label">
                <span>Email</span>
                <Input
                  className={cn(profileFieldErrors.email && 'border-destructive')}
                  type="email"
                  value={profileForm.email}
                  onChange={e => {
                    setProfileForm(prev => ({ ...prev, email: e.target.value }));
                    if (profileFieldErrors.email) {
                      setProfileFieldErrors(prev => ({ ...prev, email: '' }));
                    }
                  }}
                  required
                />
                {profileFieldErrors.email && <p className="cm-err">{profileFieldErrors.email}</p>}
              </label>
              <label className="fin-label">
                <span>Phone</span>
                <Input
                  className={cn(profileFieldErrors.phone && 'border-destructive')}
                  type="tel"
                  value={profileForm.phone}
                  onChange={e => {
                    setProfileForm(prev => ({ ...prev, phone: e.target.value }));
                    if (profileFieldErrors.phone) {
                      setProfileFieldErrors(prev => ({ ...prev, phone: '' }));
                    }
                  }}
                  placeholder="8012345678"
                />
                {profileFieldErrors.phone && <p className="cm-err">{profileFieldErrors.phone}</p>}
              </label>
              <label className="fin-label">
                <span>{isAdmin ? 'Admin ID' : 'User ID'}</span>
                <Input
                  value={displayProfile?.id ?? ''}
                  readOnly
                  disabled
                />
              </label>
            </div>
            {modalError && (
              <div className="profile-modal-note error">{modalError}</div>
            )}
            <div className="fin-modal-actions">
              <Button type="button" variant="outline" onClick={() => setEditModal(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </ProfileModal>
      )}

      {editModal === 'access' && (
        <ProfileModal
          title="Edit Access Settings"
          onClose={() => setEditModal(null)}
          disabled={saving}
        >
          <form onSubmit={handleAccessSave} className="fin-form">
            <div className="fin-form-grid">
              <label className="fin-label">
                <span>Status</span>
                <SearchableSelect
                  className="h-9"
                  value={accessForm.status}
                  options={ENTITY_STATUS_SELECT_OPTIONS}
                  onChange={(value) => setAccessForm({ status: value })}
                  placeholder="Select status"
                  searchPlaceholder="Search status…"
                  aria-label="Status"
                />
              </label>
              <label className="fin-label">
                <span>Role</span>
                <Input
                  value={displayProfile?.role ?? ''}
                  readOnly
                  disabled
                />
              </label>
              <label className="fin-label">
                <span>OTP Status</span>
                <Input
                  value={otpEnabled ? 'Enabled' : 'Disabled'}
                  readOnly
                  disabled
                />
              </label>
              <label className="fin-label">
                <span>Last Updated</span>
                <Input
                  value={formatTimestamp(displayProfile?.updatedAt)}
                  readOnly
                  disabled
                />
              </label>
            </div>
            <div className="profile-modal-note">
              OTP settings are managed by your administrator and cannot be changed from this screen.
            </div>
            {modalError && (
              <div className="profile-modal-note error">{modalError}</div>
            )}
            <div className="fin-modal-actions">
              <Button type="button" variant="outline" onClick={() => setEditModal(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </ProfileModal>
      )}

      {editModal === 'password' && (
        <ProfileModal title="Update Password" onClose={() => setEditModal(null)}>
          <div className="fin-form">
            <div className="fin-form-grid">
              <label className="fin-label">
                <span>Current Password</span>
                <Input type="password" value="••••••••" readOnly disabled />
              </label>
              <label className="fin-label">
                <span>Policy</span>
                <Input value="Min 8, max 128 characters" readOnly disabled />
              </label>
            </div>
            <div className="profile-modal-note">
              Use the forgot-password flow to reset your login password securely. You will receive a one-time code at your registered email address.
            </div>
            <Link to="/forgot-password" className="profile-modal-link" onClick={() => setEditModal(null)}>
              Go to forgot password →
            </Link>
            <div className="fin-modal-actions">
              <Button type="button" variant="outline" onClick={() => setEditModal(null)}>
                Close
              </Button>
            </div>
          </div>
        </ProfileModal>
      )}
    </div>
  );
};

export default Profile;
